import React from 'react';
import './Skeleton.css';

/*
 * Shared loading placeholders.
 *
 * The rule these follow: a skeleton is only worth showing if it occupies the
 * same space the real content will. A centred spinner tells you nothing about
 * what is coming and guarantees the page jumps when it arrives; a skeleton that
 * mirrors the layout means the content lands into a shape that already exists.
 *
 * So `TableSkeleton` takes the same column descriptors the real table uses and
 * matches its padding and row height exactly — measure them together if either
 * changes.
 *
 * Pair these with `useDeferredLoading`, which decides *whether* to show one.
 * Rendering a skeleton for a 90ms response is worse than rendering nothing.
 */

/** One placeholder block. `delay` staggers the sweep in lists (0–4). */
export const Skeleton = ({ className = '', style, delay = 0 }) => (
  <span
    aria-hidden="true"
    className={`sk block ${delay ? `sk--d${Math.min(delay, 4)}` : ''} ${className}`}
    style={style}
  />
);

/**
 * A run of text lines. The last line is shortened because real paragraphs
 * rarely end flush with the column edge, and a block of equal-length bars reads
 * as a chart rather than as prose.
 */
export const SkeletonText = ({ lines = 2, className = '', lastLineWidth = '60%' }) => (
  <span className={`flex flex-col gap-1.5 ${className}`}>
    {Array.from({ length: lines }, (_, index) => (
      <Skeleton
        key={index}
        delay={index}
        className="h-3"
        style={index === lines - 1 ? { width: lastLineWidth } : undefined}
      />
    ))}
  </span>
);

/**
 * Table body placeholder.
 *
 * `columns` accepts the same shape the table renders, each optionally carrying
 * a `skeleton` descriptor: `{ width, align, lines }`. Anything without one gets
 * a single medium bar, so adding a column never leaves a hole here.
 */
export const TableSkeleton = ({ columns, rows = 8, rowClassName = '' }) => (
  <tbody aria-hidden="true">
    {Array.from({ length: rows }, (_, rowIndex) => (
      <tr key={rowIndex} className={`border-b border-[#EEF3F4] ${rowClassName}`}>
        {columns.map((column, columnIndex) => {
          const spec = column.skeleton || {};
          const lines = spec.lines || 1;
          return (
            <td
              key={columnIndex}
              className={`px-3.5 py-3 ${spec.align === 'right' ? 'text-right' : ''}`}
            >
              <span
                className={`inline-flex flex-col gap-1.5 w-full ${
                  spec.align === 'right' ? 'items-end' : 'items-start'
                }`}
              >
                {Array.from({ length: lines }, (_, lineIndex) => (
                  <Skeleton
                    key={lineIndex}
                    delay={rowIndex % 4}
                    className={lineIndex === 0 ? 'h-3.5' : 'h-2.5'}
                    style={{
                      width:
                        lineIndex === 0
                          ? spec.width || '70%'
                          : spec.subWidth || '45%',
                    }}
                  />
                ))}
              </span>
            </td>
          );
        })}
      </tr>
    ))}
  </tbody>
);

export default Skeleton;
